<script lang="ts">
	import Icon from "../Icon.svelte";
	import Link from "../Link.svelte";
	import { contactPathMap } from "../../../../core/schema/paths/contactPathMap.js";
	import { getServiceCategoryPath } from "../../../../core/schema/paths/getServiceCategoryPath.js";
	import { Service } from "../../../../core/schema/Service.js";
	import translations from "./ContactAndServiceCTA.translations.json";
	import arrowRightLong from "../../../../core/assets/icons/arrow-right-long.svg?raw";
	import chat from "../../../../core/assets/icons/chat.svg?raw";
	import tools from "../../../../core/assets/icons/tools.svg?raw";
	import type { Contact } from "../../../../core/schema/Contact.js";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import DynamicPicture from "../Picture/DynamicPicture.svelte";
	import { getSharedTranslate } from "../../utils/getSharedTranslate.js";
	import { getSharedGlobalSettings } from "../../getSharedGlobalSettings.js";
	import { Locale } from "../../../../core/schema/Locale.js";

	const { TENANT_LOCALE } = getSharedGlobalSettings();

	let contact: Contact | undefined;
	export { contact as relatedContact };

	const translate = getSharedTranslate(translations);
</script>

<div class="mb-4 flex flex-col gap-2 xl:grid xl:grid-cols-2">
	<div class="bg-primary flex w-full flex-col justify-between gap-8 rounded-lg p-6 text-gray-300">
		<div class="flex w-full flex-col justify-start gap-8">
			<Icon icon={chat} class="h-[4.25rem] w-fit" />
			<span class="font-spaceGrotesk text-2md m-0 font-medium uppercase leading-7">{translate("contactUs")}</span>
			<p class="m-0 text-xs font-normal leading-[1.425rem] lg:w-2/3">
				{translate("contactText")}
			</p>
		</div>
		<div class="flex flex-col gap-8 md:gap-[2.5rem]">
			<Link
				asButton
				class="text-2xs font-semibold uppercase leading-[1.21] tracking-widest text-white"
				variant="transparent"
				icon={arrowRightLong}
				href="/{contactPathMap[TENANT_LOCALE]}">{translate("allContacts")}</Link
			>
			{#if contact}
				<div
					class="bg-primary-200 flex w-full flex-row-reverse justify-between gap-4 rounded p-4 sm:flex-row sm:justify-start md:p-8 lg:h-[12rem]"
				>
					<div class="hidden h-[4rem] w-[4rem] sm:block sm:h-[6rem] sm:w-[6rem] md:h-[8rem] md:w-[8rem]">
						<DynamicPicture
							bucket="storage"
							imgClass="rounded-full w-full h-full object-cover"
							class="relative aspect-square"
							loading="lazy"
							image={contact.image}
							width={200}
							height={200}
							alt={contact.name}
						/>
					</div>
					<div class="flex w-full flex-col sm:w-5/6 md:w-4/6">
						<div class="flex flex-col md:h-full">
							<p class="lg:text-2lg font-spaceGrotesk text-md mb-0 font-medium leading-tight md:mt-2 md:text-lg">
								{nbspify(contact.name)}
							</p>
							<p class="text-3xs sm:text-3xs mb-3 block uppercase leading-tight tracking-wider md:mb-4 md:text-base">
								{nbspify(contact.position)}
							</p>
						</div>
						<div class="grid grid-cols-6 md:grid-cols-5">
							{#if contact.email}
								<p class="text-3xs sm:text-2xs mb-0 hidden leading-[1.6] sm:inline-block md:text-xs">E-mail:</p>
								<span
									class="text-3xs sm:text-2xs col-span-5 mb-0 font-semibold leading-[1.6] underline underline-offset-2 md:col-span-4 md:text-xs"
									><a href="mailto:{contact.email}">{nbspify(contact.email)}</a></span
								>
							{/if}
							{#if contact.phone}
								<p class="text-3xs sm:text-2xs mb-0 hidden leading-[1.6] sm:inline-block md:text-xs">
									{translate("phone")}:
								</p>
								<span class="text-3xs sm:text-2xs col-span-5 mb-0 font-semibold leading-[1.6] md:col-span-4 md:text-xs"
									><a href="tel:{contact.phone}">{nbspify(contact.phone)}</a></span
								>
							{/if}
						</div>
					</div>
				</div>
			{/if}
		</div>
	</div>
	<div class="bg-primary flex w-full flex-col justify-between gap-8 rounded-lg p-6 text-gray-300">
		<div class="flex w-full flex-col justify-start gap-8">
			<Icon icon={tools} class="h-[4.25rem] w-fit" />
			<span class="font-spaceGrotesk text-2md m-0 font-medium uppercase leading-7">{translate("onServicePage")}</span>
			<p class="m-0 text-xs font-normal leading-[1.425rem] lg:w-2/3">
				{translate("serviceText")}
			</p>
		</div>
		<div class="flex flex-col gap-8 md:gap-[2.5rem]">
			<Link
				aria-label={translate("moreInfoAria", {}, true)}
				asButton
				class="text-2xs font-semibold uppercase leading-[1.21] tracking-widest text-white"
				variant="transparent"
				icon={arrowRightLong}
				href="/{getServiceCategoryPath(Service.Service)[TENANT_LOCALE]}"
			>
				{translate("moreInfo")}
			</Link>
			<div class="bg-primary-200 flex w-full gap-4 rounded p-4 md:p-8 lg:h-[12rem]">
				<div class="flex w-full flex-col justify-between gap-4 md:w-4/6">
					<div class="flex flex-col md:h-full">
						<p class="lg:text-2lg font-spaceGrotesk text-md mb-0 font-medium leading-tight md:mt-2 md:text-lg">
							{translate("serviceSupport")}
						</p>
					</div>
					<div class="grid grid-cols-6 md:grid-cols-5">
						<p class="text-3xs sm:text-2xs mb-0 hidden leading-[1.6] sm:inline-block md:text-xs">E-mail:</p>
						<span
							class="text-3xs sm:text-2xs col-span-5 mb-0 font-semibold leading-[1.6] underline underline-offset-2 md:col-span-4 md:text-xs"
							><a href="mailto:hotline@vertiflex.eu">hotline@vertiflex.eu</a></span
						>
						<p class="text-3xs sm:text-2xs mb-0 hidden leading-[1.6] sm:inline-block md:text-xs">
							{translate("phone")}:
						</p>
						<span class="text-3xs sm:text-2xs col-span-5 mb-0 font-semibold leading-[1.6] md:col-span-4 md:text-xs">
							{#if TENANT_LOCALE === Locale.pl}
								<a href="tel:+48501525152">+48 501 525 152</a>{:else}<a href="tel:+420554818300">+420 554 818 300</a>
							{/if}
						</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</div>
